import { Controller } from "@hotwired/stimulus";
import { default as CodeMirror } from "~/stimulus/controllers/codemirror";
import "codemirror/mode/javascript/javascript";

export default class extends Controller<HTMLElement> {
  static targets = [
    "payload",
    "headers",
    "expression",
    "submit",
    "loading",
    "success",
    "error",
    "successContent",
    "errorTitle",
    "errorContent",
  ];
  static values = {
    evaluateUrl: String,
  };

  declare readonly payloadTarget: HTMLTextAreaElement;
  declare readonly headersTarget: HTMLTextAreaElement;
  declare readonly expressionTarget: HTMLTextAreaElement;
  declare readonly submitTarget: HTMLButtonElement;

  declare readonly loadingTarget: HTMLElement;
  declare readonly successTarget: HTMLElement;
  declare readonly errorTarget: HTMLElement;

  declare readonly successContentTarget: HTMLElement;
  declare readonly errorTitleTarget: HTMLElement;
  declare readonly errorContentTarget: HTMLElement;
  declare readonly evaluateUrlValue: string;

  payloadInput: CodeMirror.EditorFromTextArea | null = null;
  headersInput: CodeMirror.EditorFromTextArea | null = null;
  expressionInput: CodeMirror.EditorFromTextArea | null = null;

  connect() {
    this.payloadInput = CodeMirror.fromTextArea(this.payloadTarget, {
      mode: "javascript",
      lineNumbers: true,
      tabSize: 2,
      lineWrapping: true,
    });
    this.formatJSON(this.payloadInput);

    this.headersInput = CodeMirror.fromTextArea(this.headersTarget, {
      mode: "javascript",
      lineNumbers: true,
      tabSize: 2,
      lineWrapping: true,
    });
    this.formatJSON(this.headersInput);

    this.expressionInput = CodeMirror.fromTextArea(this.expressionTarget, {
      mode: "text/plain",
      lineNumbers: true,
      lineWrapping: true,
    });
  }

  async evaluate(event: Event) {
    event.preventDefault();

    if (!this.payloadInput || !this.headersInput || !this.expressionInput) {
      return;
    }

    const payload = this.payloadInput.getValue();
    const headers = this.headersInput.getValue();
    const expression = this.expressionInput.getValue();

    this.submitTarget.disabled = true;
    this.showState("loading");

    try {
      const response = await fetch(this.evaluateUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken(),
        },
        body: JSON.stringify({ payload, headers, expression }),
      });

      const data = await response.json();

      if (data.success) {
        this.successContentTarget.textContent = JSON.stringify(data.result, null, 2);
        this.showState("success");
      } else {
        this.errorTitleTarget.textContent = "Evaluation Error";
        this.errorContentTarget.textContent = data.error;
        this.showState("error");
      }
    } catch (error) {
      this.errorTitleTarget.textContent = "Request Error";
      this.errorContentTarget.textContent = error instanceof Error ? error.message : String(error);
      this.showState("error");
    } finally {
      this.submitTarget.disabled = false;
    }
  }

  private csrfToken(): string {
    const meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.getAttribute("content") || "" : "";
  }

  private formatJSON(editor: CodeMirror.EditorFromTextArea): void {
    try {
      const value = editor.getValue().trim();
      if (value) {
        const parsed = JSON.parse(value);
        const formatted = JSON.stringify(parsed, null, 2);
        editor.setValue(formatted);
      }
    } catch (err) {
      // If it's not valid JSON, leave it as is
    }
  }

  private showState(state: "loading" | "success" | "error"): void {
    this.loadingTarget.classList.toggle("hidden", state !== "loading");
    this.successTarget.classList.toggle("hidden", state !== "success");
    this.errorTarget.classList.toggle("hidden", state !== "error");
  }
}
